"use client";

import React, { useCallback } from "react";
import { Accordion, Box, Flex, Link } from "@iris/react-components";
import {
  statesNames,
  stateAbbreviations,
  carsCities,
  trucksCities,
  privateSellerCarsLinks,
} from "../../constants/myArea";
import { atcBaseUrl } from "@/constants/config";
import { sendClick } from "reaxl-analytics";
import BlpAccordionPanel from "./BlpAccordionPanel";

const MY_AREA_TRIGGER_TITLE = "iris:font-bold iris:text-[24px]";

export default function BuyerLandingMyAreaSection({
  collapseAll = false,
}: {
  collapseAll?: boolean;
} = {}) {
  const onStateClick = useCallback((event: React.MouseEvent, state: string) => {
    sendClick("howToSellACarInStateClick", event, { state });
  }, []);

  // Factory for one-arg analytics-only handlers.
  const track = useCallback(
    (name: string) => (event: React.MouseEvent) => sendClick(name, event),
    []
  );

  const onPrivateSellerCarsInMyAreaClick = track("privateSellerCarsInMyArea");
  const onPrivateSellerTrucksInMyAreaClick = track("privateSellerTrucksInMyArea");
  const onPrivateSellerCarsClick = track("privateSellerCars");
  const onSellMyCarsClick = track("sellMyCars");
  const onPreQualifyClick = track("preQualify");
  const onAutoGAPInsuranceClick = track("autoGAPInsurance");
  const onVehicleProtectionClick = track("vehicleProtection");
  const onFAQsClick = track("FAQs");
  const onBuyerGuideClick = track("buyerGuide");
  const onSellerGuideClick = track("sellerGuide");

  const popularServicesCol0 = [
    {
      href: `${atcBaseUrl}/cars-for-sale/by-owner`,
      label: "Private Seller Cars",
      onClick: onPrivateSellerCarsClick,
    },
    {
      href: `${atcBaseUrl}/sell-my-car`,
      label: "Sell My Cars",
      onClick: onSellMyCarsClick,
    },
    {
      href: `${atcBaseUrl}/marketplace/financing`,
      label: "Pre Qualify",
      onClick: onPreQualifyClick,
    },
    {
      href: `${atcBaseUrl}/marketplace/gap-insurance`,
      label: "Auto GAP Insurance",
      onClick: onAutoGAPInsuranceClick,
    },
    {
      href: `${atcBaseUrl}/marketplace/vehicle-service-contracts`,
      label: "Vehicle Protection",
      onClick: onVehicleProtectionClick,
    },
  ];

  const popularServicesCol1 = [
    {
      href: `${atcBaseUrl}/marketplace/faqs`,
      label: "FAQs",
      onClick: onFAQsClick,
    },
    {
      href: `${atcBaseUrl}/marketplace/buyer-guide`,
      label: "Buyer Guide",
      onClick: onBuyerGuideClick,
    },
    {
      href: `${atcBaseUrl}/marketplace/seller-guide`,
      label: "Seller Guide",
      onClick: onSellerGuideClick,
    },
  ];

  return (
    <Box className="buyer-landing-my-area-section iris:bg-white iris:pt-12">
      <Accordion variant="uncontained" defaultValue={collapseAll ? [] : ["panel4"]}>
        <BlpAccordionPanel value="panel1" title="Private Seller Cars in My Area" titleClassName={MY_AREA_TRIGGER_TITLE}>
          {/* iris-override (token unavailable): Iris Link defaults to constrained flex width; using iris:!w-full and iris:!justify-start on all accordion link lists in this component */}
          <ul className="my-area-link-list">
            {carsCities.map((city) => (
              <li key={city.path}>
                <Link
                  href={`${atcBaseUrl}/cars-for-sale/by-owner/${city.path}`}
                  className="iris:leading-loose iris:!w-full iris:focus:inline iris:!justify-start"
                  onClick={onPrivateSellerCarsInMyAreaClick}
                >
                  {`Cars for Sale by Owner in ${city.name}`}
                </Link>
              </li>
            ))}
          </ul>
        </BlpAccordionPanel>

        <BlpAccordionPanel value="panel2" title="Private Seller Trucks in My Area" titleClassName={MY_AREA_TRIGGER_TITLE}>
          <ul className="my-area-link-list">
            {trucksCities.map((city) => (
              <li key={city.path}>
                <Link
                  href={`${atcBaseUrl}/cars-for-sale/by-owner/truck/${city.path}`}
                  className="iris:leading-loose iris:!w-full iris:focus:inline iris:!justify-start"
                  onClick={onPrivateSellerTrucksInMyAreaClick}
                >
                  {`Trucks for Sale by Owner in ${city.name}`}
                </Link>
              </li>
            ))}
          </ul>
        </BlpAccordionPanel>

        <BlpAccordionPanel value="panel5" title="Private Seller Cars by Price" titleClassName={MY_AREA_TRIGGER_TITLE}>
          <ul className="my-area-link-list">
            {privateSellerCarsLinks.map((link) => (
              <li key={link.path}>
                <Link
                  href={`${atcBaseUrl}${link.path}`}
                  className="iris:leading-loose iris:!w-full iris:focus:inline iris:!justify-start"
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </BlpAccordionPanel>

        <BlpAccordionPanel value="panel3" title="How to Sell a Car in..." titleClassName={MY_AREA_TRIGGER_TITLE}>
          <ul className="my-area-link-list">
            {statesNames.map((name, idx) => (
              <li key={stateAbbreviations[idx]}>
                <Link
                  href={`${atcBaseUrl}/sell-my-car/in-${stateAbbreviations[idx]}`}
                  className="iris:leading-loose iris:!w-full iris:focus:inline iris:!justify-start"
                  onClick={(event: React.MouseEvent) =>
                    onStateClick(event, stateAbbreviations[idx])
                  }
                >
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        </BlpAccordionPanel>

        <BlpAccordionPanel value="panel4" title="Popular Services and Tips" titleClassName={MY_AREA_TRIGGER_TITLE}>
          <Flex className="iris:flex-wrap iris:pl-[4%]">
            <Flex direction="col" gap="none" className="iris:shrink iris:grow-0 iris:basis-[20%]">
              {popularServicesCol0.map(({ href, label, onClick }) => (
                <Link
                  key={href}
                  href={href}
                  className="iris:leading-loose iris:!w-full iris:focus:inline iris:!justify-start"
                  onClick={onClick}
                >
                  {label}
                </Link>
              ))}
            </Flex>
            <Flex direction="col" gap="none" className="iris:shrink iris:grow-0 iris:basis-[20%]">
              {popularServicesCol1.map(({ href, label, onClick }) => (
                <Link
                  key={href}
                  href={href}
                  className="iris:leading-loose iris:!w-full iris:focus:inline iris:!justify-start"
                  onClick={onClick}
                >
                  {label}
                </Link>
              ))}
            </Flex>
          </Flex>
        </BlpAccordionPanel>
      </Accordion>
    </Box>
  );
}
